import { ChakraProvider } from '@chakra-ui/react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { appConfig } from 'app/config/appConfig';
import {
  getJwtLocalStorage,
  getLocalStorage,
  setLocalStorage,
} from 'app/helpers/local-storage';
import axios from 'axios';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
// ToastContainer will be lazy loaded
import { getPageHeaderApi } from './apis/pageHeaders';
import { FooterPlayer } from './components/FooterPlayer/Loadable';
import { Loading } from './components/Loading/Loadable';
import theme from './config/theme';
import {
  COUNTRY,
  IP_ADDRESS,
  IS_REDIRECT_URL,
  REDIRECT_URL,
} from './constants';
import { PageHeaderContext } from './contexts/PageHeaderContext';
import { socket, WebsocketProvider } from './contexts/WebsocketContext';
import { usePlayers } from './hooks/player/usePlayers';
import { useSubscriptions } from './hooks/subscription/useSubscriptions';
import { useWishlists } from './hooks/wishlist/useWishlists';
import DownloadQueueProcessor from './components/DownloadQueueProcessor';
import CheckNeedConfirmContributorMiddleware from './middleware/CheckNeedConfirmContributorMiddleware';
import { useAuthSlice } from './pages/Login/slice';
import { selectAuth } from './pages/Login/slice/selectors';
import { RenderRoutes } from './routes/RenderRoutes';
import { LazyToastContainer } from './components/LazyToastContainer';

export function App() {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { actions } = useAuthSlice();
  const { isSessionFetched } = useSelector(selectAuth);
  const { isAudioPlay, playingTrack, isUseInput } = usePlayers();
  const { getMySubscription } = useSubscriptions();
  const [pageHeader, setPageHeader] = useState<any>(null);
  const { onGetMyWishlists } = useWishlists();
  const clientId = appConfig.googleOAuthClientId || '';

  const getData = async () => {
    try {
      const res = await axios.get('https://ipinfo.io/json');
      const ipAddress = res?.data?.ip;
      const country = res?.data?.country;

      // Ensure we have valid string values before storing
      setLocalStorage(
        IP_ADDRESS,
        typeof ipAddress === 'string' && ipAddress.trim() !== ''
          ? ipAddress
          : 'unknown',
      );
      setLocalStorage(
        COUNTRY,
        typeof country === 'string' && country.trim() !== ''
          ? country
          : 'unknown',
      );
    } catch (error) {
      console.warn(
        'Failed to fetch IP address and country information:',
        error,
      );
      // Set fallback values if the API call fails
      setLocalStorage(IP_ADDRESS, 'unknown');
      setLocalStorage(COUNTRY, 'unknown');
    }
  };

  const getPageHeader = async () => {
    const data: any = await getPageHeaderApi();
    if (data) {
      setPageHeader(data?.data);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (isSessionFetched) {
      getPageHeader();
      getMySubscription();
    }
  }, [dispatch, getMySubscription, isSessionFetched]);

  useEffect(() => {
    if (isSessionFetched) {
      onGetMyWishlists();
    }
  }, [isSessionFetched, onGetMyWishlists]);

  useEffect(() => {
    const accessToken = getJwtLocalStorage();
    if (
      (!window.location.pathname.startsWith('/auth') && !isSessionFetched) ||
      (window.location.pathname.startsWith('/auth') && !!accessToken)
    ) {
      dispatch(actions.getMe());
    }
  }, [actions, dispatch, isSessionFetched, onGetMyWishlists]);

  useEffect(() => {
    const isRedirect = getLocalStorage(IS_REDIRECT_URL);
    if (
      !window.location.pathname.startsWith('/auth') &&
      !isSessionFetched &&
      !isRedirect
    ) {
      setLocalStorage(REDIRECT_URL, window.location.href);
    }
  }, [actions, dispatch, isSessionFetched, onGetMyWishlists]);

  useEffect(() => {
    socket.on('connect', () => {
      console.log('Connected!');
    });

    return () => {
      socket.off('connect');
    };
  }, []);

  // Fix back/forward cache: Cleanup on pagehide
  useEffect(() => {
    const handlePageHide = () => {
      // Cleanup any resources that might prevent bfcache
      socket.disconnect();
    };

    const handlePageShow = () => {
      // Reconnect if needed
      if (!socket.connected) {
        socket.connect();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const handleKeyPress = useCallback(
    event => {
      const { key } = event;
      if ([' '].includes(key) && playingTrack && !isUseInput) {
        event.preventDefault();
      }
    },
    [isUseInput, playingTrack],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <ChakraProvider theme={theme}>
      <GoogleOAuthProvider clientId={clientId}>
        <PageHeaderContext.Provider
          value={{
            pageHeader,
          }}
        >
          <WebsocketProvider value={socket}>
            <BrowserRouter>
              <Helmet
                titleTemplate="%s - Crooklyn Clan"
                defaultTitle="Crooklyn Clan"
                htmlAttributes={{ lang: i18n.language }}
              >
                <meta name="description" content="Crooklyn Clan" />
              </Helmet>
              <CheckNeedConfirmContributorMiddleware />

              <DownloadQueueProcessor />

              <RenderRoutes />

              <Suspense fallback={null}>
                <LazyToastContainer />
              </Suspense>
              {isAudioPlay && (
                <Suspense fallback={null}>
                  <FooterPlayer />
                </Suspense>
              )}
              <Loading />
            </BrowserRouter>
          </WebsocketProvider>
        </PageHeaderContext.Provider>
      </GoogleOAuthProvider>
    </ChakraProvider>
  );
}
